import { Field, Radio, RadioGroup } from "@headlessui/react";
import { type VariantProps, cva } from "class-variance-authority";
import { forwardRef } from "react";
import { Text } from "../../typography/Text";
import { classNames } from "../../utils/classNames";
import { radioCircleInnerStyles, radioCircleStyles } from "./BaseRadio";

export const radioCardStyles = cva(
	["flex", "flex-col", "rounded-lg", "transition-all", "duration-200", "bg-white", "overflow-hidden", "border-2"],
	{
		variants: {
			checked: {
				true: "border-primary",
				false: "border-neutral",
			},
			disabled: {
				true: "cursor-not-allowed",
				false: "",
			},
			fill: {
				true: "h-full",
				false: "",
			},
		},
		compoundVariants: [{ checked: false, disabled: false, className: "cursor-pointer hover:bg-neutral-faded" }],
		defaultVariants: {
			disabled: false,
			fill: false,
		},
	},
);

const radioCardHeaderStyles = cva(["flex", "transition-colors"], {
	variants: {
		checked: {
			true: "",
			false: "",
		},
		variant: {
			header: "gap-3 px-3 py-2",
			inline: "gap-4 px-4 pt-4 pb-1",
		},
	},
	compoundVariants: [
		{ variant: "header", checked: true, className: "bg-primary-faded" },
		{ variant: "header", checked: false, className: "bg-neutral-faded" },
	],
	defaultVariants: {
		variant: "header",
	},
});

const radioCardContentStyles = cva([], {
	variants: {
		variant: {
			header: "p-4",
			inline: "pr-4 pb-4 pl-[52px]",
		},
	},
	defaultVariants: {
		variant: "header",
	},
});

const radioGroupStyles = cva(["flex"], {
	variants: {
		orientation: {
			horizontal: "gap-8",
			vertical: "flex-col",
		},
	},
	defaultVariants: {
		orientation: "vertical",
	},
});

const radioCardContentControlSelector = "a, button, input, select, textarea, [role='button']";

export type RadioCardOption = {
	label: string | React.ReactNode;
	value: string;
	disabled?: boolean;
	content?: React.ReactNode;
	description?: string;
	children?: React.ReactNode | ((props: { checked: boolean }) => React.ReactNode);
	"aria-label"?: string;
	"aria-invalid"?: boolean;
	onClick?: () => void;
};

export type BaseRadioCardProps = {
	value?: string;
	options: RadioCardOption[];
	onChange?: (value: string) => void;
	name: string;
	orientation?: VariantProps<typeof radioGroupStyles>["orientation"];
	variant?: VariantProps<typeof radioCardHeaderStyles>["variant"];
	itemClassName?: string;
	"aria-label"?: string;
	"aria-labelledby"?: string;
};

export const BaseRadioCardInner = (
	{
		value,
		onChange,
		name,
		options = [],
		orientation,
		variant = "header",
		itemClassName,
		"aria-label": ariaLabel,
		"aria-labelledby": ariaLabelledBy,
	}: BaseRadioCardProps,
	ref: React.ForwardedRef<HTMLDivElement>,
) => {
	const handleFocus = (e: React.FocusEvent<HTMLDivElement>) => {
		if (e.target !== e.currentTarget) {
			return;
		}
		// Hand focus into the group: Headless UI's roving-tabindex entry (the
		// checked or first enabled radio), falling back to any enabled radio.
		const radio =
			e.currentTarget.querySelector<HTMLElement>('[role="radio"][tabindex="0"]') ??
			e.currentTarget.querySelector<HTMLElement>(
				'[role="radio"]:not([aria-disabled="true"]):not([data-disabled])',
			);
		radio?.focus();
	};

	return (
		// Focus target for react-hook-form's focus-on-error (scrolls the group into
		// view). Headless UI strips ref/tabIndex from RadioGroup's own div.
		<div ref={ref} tabIndex={-1} onFocus={handleFocus}>
			<RadioGroup
				name={name}
				value={value}
				onChange={onChange}
				className={radioGroupStyles({ orientation })}
				aria-label={ariaLabel}
				aria-labelledby={ariaLabelledBy}
			>
				{options.map((o) => {
					const isChecked = value === o.value;
					const hasExtraContent = o.content || o.description || o.children;

					const handleContentSelect = (e: React.MouseEvent | React.KeyboardEvent) => {
						if (o.disabled) {
							return;
						}
						const target = e.target as HTMLElement;
						if (target.closest(radioCardContentControlSelector)) {
							return;
						}
						onChange?.(o.value);
						o.onClick?.();
					};

					const handleContentKeyDown = (e: React.KeyboardEvent) => {
						const target = e.target as HTMLElement;
						if (target.closest(radioCardContentControlSelector)) {
							e.stopPropagation();
							return;
						}
						if (e.key === "Enter" || e.key === " ") {
							e.preventDefault();
							handleContentSelect(e);
						}
					};

					return (
						<Field key={o.value} className={classNames("my-1", itemClassName)}>
							<div
								className={radioCardStyles({
									checked: isChecked,
									disabled: Boolean(o.disabled),
									fill: true,
								})}
								onClick={handleContentSelect}
								onKeyDown={handleContentKeyDown}
							>
								<Radio
									key={o.value}
									value={o.value}
									aria-label={o["aria-label"]}
									aria-invalid={o["aria-invalid"]}
									onClick={o.onClick}
									disabled={o.disabled}
								>
									{({ checked }) => (
										<div className={radioCardHeaderStyles({ checked, variant })}>
											<span className={radioCircleStyles({ checked, variant })}>
												{checked && <span className={radioCircleInnerStyles({ variant })} />}
											</span>
											<Text
												size="sm"
												weight={variant === "header" ? "medium" : "normal"}
												className={classNames("w-full", o.disabled ? "opacity-50" : "")}
											>
												{o.label}
											</Text>
										</div>
									)}
								</Radio>
								{hasExtraContent && (
									<div
										className={radioCardContentStyles({ variant })}
										onKeyDown={(e) => {
											const target = e.target as HTMLElement;
											if (!target.closest(radioCardContentControlSelector)) {
												return;
											}
											if (
												e.key === "ArrowUp" ||
												e.key === "ArrowDown" ||
												e.key === "ArrowLeft" ||
												e.key === "ArrowRight"
											) {
												e.stopPropagation();
											}
										}}
									>
										{o.content ?? (
											<div className="flex flex-col gap-1">
												{o.description && (
													<Text size="sm" intent="muted">
														{o.description}
													</Text>
												)}
												{o.children && (
													<div>
														{typeof o.children === "function"
															? o.children({ checked: isChecked })
															: o.children}
													</div>
												)}
											</div>
										)}
									</div>
								)}
							</div>
						</Field>
					);
				})}
			</RadioGroup>
		</div>
	);
};

BaseRadioCardInner.displayName = "BaseRadioCard";

export const BaseRadioCard = forwardRef(BaseRadioCardInner);
